import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  Layers,
  ShoppingCart,
  Banknote,
  Package,
  UserPlus,
  FolderTree,
  Settings,
  ChevronRight,
  ChevronLeft,
  DollarSign,
  CreditCard,
  FileText,
  LogOut,
  UserRound,
  History,
} from "lucide-react";
import { useDispatch } from "react-redux";
import { logout } from "../../RTK/login/Authentication";
import { usePermissions } from "../../contexts/PermissionsContext";

const SideBar = ({
  isCollapsed,
  setIsCollapsed,
  isMobileView,
  toggleNav,
  setToggleNav,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const Dispatch = useDispatch();
  const { hasPermission } = usePermissions(); // ✅ استخدام hook الصلاحيات

  // ✅ تعريف الصفحات مع صلاحياتها
  const navigationGroups = {
    main: [
      {
        name: "Dashboard",
        arabicName: "لوحة التحكم",
        link: "/",
        icon: LayoutDashboard,
        permission: "view_dashboard_stats",
      },
      {
        name: "Manage Users",
        arabicName: "المستخدمين",
        link: "/user",
        icon: Users,
        permission: "view_all_users",
      },
      {
        name: "User Levels",
        arabicName: "المستويات",
        link: "/user-levels",
        icon: Layers,
        permission: "view_all_users",
      },
      {
        name: "ExpensesSummary",
        arabicName: "المصروفات",
        link: "/expenses-summary",
        icon: FileText,
        permission: "view_expenses",
      },
    ],
    orders: [
      {
        name: "Manage Orders",
        arabicName: "الطلبات",
        link: "/order",
        icon: ShoppingCart,
        permission: "view_all_orders",
      },
      {
        name: "Transactions",
        arabicName: "المعاملات",
        link: "/Transaction",
        icon: Banknote,
        permission: "view_all_transactions",
      },
    ],
    finance: [
      {
        name: "ProfitMargin",
        arabicName: "التقرير المالى",
        link: "/profit-margin",
        icon: DollarSign,
        permission: "view_profit_reports",
      },
      {
        name: "PaymentMethods",
        arabicName: "طرق الدفع",
        link: "/Payment-methods",
        icon: CreditCard,
        permission: "view_payment_methods",
      },
    ],
    inventory: [
      {
        name: "Manage Products",
        arabicName: "المنتجات",
        link: "/product",
        icon: Package,
        permission: "view_products",
      },
      {
        name: "Categories",
        arabicName: "التصنيفات",
        link: "/categories",
        icon: FolderTree,
        permission: "view_banners",
      },
      {
        name: "Employees",
        arabicName: "الموظفين",
        link: "/employees",
        icon: UserRound,
        permission: null, // ✅ متاح للجميع
      },
      {
        name: "ActivityLogs",
        arabicName: "السجلات",
        link: "/activity-logs",
        icon: History,
        permission: "view_dashboard_stats",
      },
      {
        name: "Our Agents",
        arabicName: "وكلاؤنا",
        link: "/agent",
        icon: UserPlus,
        permission: "view_agents",
      },
      {
        name: "Settings",
        arabicName: "الإعدادات",
        link: "/settings",
        icon: Settings,
        permission: "view_website_settings",
      },
    ],
  };

  // ✅ فلترة الصفحات حسب الصلاحيات
  const filterByPermission = (links) => {
    return links.filter((link) => {
      // لو مافيش صلاحية مطلوبة (null) يعني متاح للجميع
      if (link.permission === null) return true;
      // لو في صلاحية، نفحصها
      return hasPermission(link.permission);
    });
  };

  const NavLink = ({ link }) => {
    const isActive = location.pathname === link.link;
    const Icon = link.icon;

    return (
      <Link
        to={link.link}
        onClick={() => isMobileView && setToggleNav(false)}
        className={`flex items-center gap-2 p-2 rounded-xl transition-all duration-300 group
          ${
            isActive
              ? "bg-gradient-to-r from-yellow-500 to-yellow-400 text-gray-900 shadow-lg shadow-yellow-500/10"
              : "text-gray-400 hover:bg-gray-800/80 hover:text-yellow-500"
          }
        `}
      >
        <div
          className={`p-1.5 rounded-lg ${
            isActive ? "bg-white/10" : "bg-gray-800/80"
          }`}
        >
          <Icon className={`w-4 h-4 ${isActive ? "text-gray-900" : ""}`} />
        </div>
        <span
          className={`${
            isCollapsed ? "hidden" : "block"
          } text-sm font-medium whitespace-nowrap`}
        >
          {link.arabicName}
        </span>
        {isCollapsed && (
          <div className="absolute right-full mr-3 px-2 py-1.5 bg-gray-800 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-opacity invisible group-hover:visible whitespace-nowrap z-50">
            {link.arabicName}
          </div>
        )}
      </Link>
    );
  };

  const renderNavGroup = (title, links, isLast = false) => {
    // ✅ فلترة الصفحات حسب الصلاحيات
    const filteredLinks = filterByPermission(links);

    // ✅ لو مافيش صفحات متاحة، نخفي المجموعة كاملة
    if (filteredLinks.length === 0) return null;

    return (
      <div className="relative">
        <div className={`pb-3 ${!isLast ? "mb-3" : ""}`}>
          {!isCollapsed && (
            <h3 className="text-[10px] font-medium text-gray-500/80 px-2 mb-1.5 uppercase tracking-wider">
              {title}
            </h3>
          )}
          <div
            className={`grid ${
              isCollapsed ? "grid-cols-1" : "grid-cols-2"
            } gap-1.5`}
          >
            {filteredLinks.map((link, index) => (
              <NavLink key={index} link={link} />
            ))}
          </div>
        </div>
        {!isLast && (
          <div className="absolute bottom-0 left-0 right-0">
            <div className="h-[1px] bg-gradient-to-r from-transparent via-yellow-500/10 to-transparent" />
            <div className="h-[1px] bg-gradient-to-r from-transparent via-gray-700/50 to-transparent mt-[1px]" />
          </div>
        )}
      </div>
    );
  };

  const handleLogout = () => {
    Dispatch(logout());
    navigate("/login");
  };

  return (
    <>
      {toggleNav && isMobileView && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setToggleNav(false)}
        />
      )}

      <aside
        className={`fixed top-0 bottom-0 right-0 z-50 border-x bg-gradient-to-br from-gray-900 via-gray-900 to-gray-950 border-l border-gray-500/30 transition-all duration-300
          ${isCollapsed ? "w-16" : "w-64"}
          ${toggleNav || !isMobileView ? "translate-x-0" : "translate-x-full"}
        `}
        dir="rtl"
      >
        {/* Header */}
        <div className="h-16 flex items-center justify-between px-3 border-b border-gray-800/30 bg-gray-900/50">
          {!isCollapsed && (
            <h1 className="text-white font-bold text-lg">لوحة التحكم</h1>
          )}
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="p-1.5 rounded-lg bg-gray-800/80 text-gray-400 hover:text-yellow-500 hover:bg-gray-700/80 transition-colors"
          >
            {isCollapsed ? (
              <ChevronLeft className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
          </button>
        </div>

        {/* Navigation */}
        <div className="p-2">
          {renderNavGroup("القائمة الرئيسية", navigationGroups.main)}
          {renderNavGroup("إدارة الطلبات", navigationGroups.orders)}
          {renderNavGroup("المالية", navigationGroups.finance)}
          {renderNavGroup(
            "المخزون والإعدادات",
            navigationGroups.inventory,
            true,
          )}
        </div>

        {/* Logout Button */}
        <div className="p-2 border-t border-gray-800/30">
          <button
            onClick={handleLogout}
            className={`w-full flex items-center gap-2 p-2 rounded-xl transition-all duration-300 group
               bg-gray-800/80 text-gray-400 hover:bg-gray-950/80 hover:text-white`}
          >
            <div className="p-1.5 rounded-lg bg-gray-800/80">
              <LogOut className="w-4 h-4" />
            </div>
            <span
              className={`${
                isCollapsed ? "hidden" : "block"
              } text-sm font-medium whitespace-nowrap`}
            >
              تسجيل الخروج
            </span>
            {isCollapsed && (
              <div className="absolute right-full mr-3 px-2 py-1.5 bg-gray-800 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-opacity invisible group-hover:visible whitespace-nowrap z-50">
                تسجيل الخروج
              </div>
            )}
          </button>
        </div>
      </aside>
    </>
  );
};

export default SideBar;
